import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTriggerSectionIsViewedGaEvent } from 'core/hooks/useTriggerSectionIsViewedGaEvent';

import { selectSiteUrl, selectFormSecurityToken } from 'core/selectors/app';
import { selectCookieDomain, selectIsHttps } from 'core/selectors/env';
import { usePrevious } from 'core/hooks/usePrevious';

import { getEmailPopupCookie, setEmailPopupCookie } from 'core/utils/helpers/emailPopupCookie';
import dayjs from 'core/utils/dayjs';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import EmailOptInSubscribeForm from 'core/modules/Forms/EmailOptInSubscribeForm';
import Switcher from 'core/modules/Partials/Switcher/Switcher';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';

import orgConfig from 'org/configs/org.config';
import { triggerEmailOptInSectionIsViewed, triggerEmailOptInChangedGaEvent } from './actions';
import config from './configs';

import './EmailOptIn.scss';

const emailPopupCookie = getEmailPopupCookie();

function EmailOptIn() {
  const dispatch = useDispatch();
  const isSubcribedInitialy = useRef(emailPopupCookie.emailGlobalOptOut === '0');
  const emailOptInRef = useRef();
  const [hasUserCheckedOptIn, setHasUserCheckedOptIn] = useState(false);
  const prevHasUserCheckedOptIn = usePrevious(hasUserCheckedOptIn);
  const cookieDomain = useSelector(selectCookieDomain);
  const isHttps = useSelector(selectIsHttps);
  const siteUrl = useSelector(selectSiteUrl);
  const securityToken = useSelector(selectFormSecurityToken);

  const { earnType } = orgConfig;

  const {
    emailOptionsSubscribe, emailOptionsUnsubscribe, getMarketingProgramName, form, formAction,
  } = config;
  const marketingProgramName = getMarketingProgramName();

  const renderMessage = () =>
    <>Don't hunt for sales or extra {marketingProgramName} {earnType} offers. We'll email them to you.</>;

  const handleCookieUpdate = (optedIn) => {
    emailPopupCookie.seenCount += 1;
    emailPopupCookie.emailGlobalOptOut = optedIn ? '0' : '1';
    emailPopupCookie.showPopup = false;
    emailPopupCookie.lastSeen = dayjs();
    emailPopupCookie.hasSeen = true;
    setEmailPopupCookie({ emailPopupCookie, cookieDomain, isHttps });
  };

  const handleOptinChange = () => {
    const isUserCheckedOptIn = !hasUserCheckedOptIn;
    setHasUserCheckedOptIn(isUserCheckedOptIn);
    handleCookieUpdate(isUserCheckedOptIn);
    dispatch(triggerEmailOptInChangedGaEvent(isUserCheckedOptIn));
  };

  const emailOptions = hasUserCheckedOptIn ? emailOptionsSubscribe : emailOptionsUnsubscribe;

  const dispatchTriggerSectionIsViewed = () => dispatch(triggerEmailOptInSectionIsViewed());
  useTriggerSectionIsViewedGaEvent(emailOptInRef, dispatchTriggerSectionIsViewed);

  if (isSubcribedInitialy.current) {
    return null;
  }

  return (
    <div
      className={`mn_emailOptInWrapper ${hasUserCheckedOptIn ? 'mn_emailOptedIn' : ''}`}
      ref={emailOptInRef}
    >
      <ContentWrap>
        {!!prevHasUserCheckedOptIn !== hasUserCheckedOptIn && <EmailOptInSubscribeForm
          key={emailOptions.email_global_optout} // Rerender component on subscribe change to trigger Ajax request
          useAjax
          siteUrl={siteUrl}
          securityToken={securityToken}
          form={form}
          emailOptions={emailOptions}
          callback={() => (null)}
          formAction={formAction}
        />}
        <div className="mn_optInImage" />
        <h2>
          {hasUserCheckedOptIn
            ? <>Nice work<span className="mn_exclamation">!</span></>
            : <>Get <span>the</span> best deals right <span>in</span> your inbox<span className="mn_dot">.</span></>
          }
        </h2>
        <p>
          {hasUserCheckedOptIn
            ? 'Emails with the best deals are on the way.'
            : renderMessage()
          }
        </p>
        <div className="mn_switcherWrapper">
          <label htmlFor="mn_switcher">
            <Switcher onClickHandler={handleOptinChange} isChecked={hasUserCheckedOptIn} />
            <span className="mn_switchLabelText">Yes, email me about deals.</span>
          </label>
        </div>
        <div className="mn_decorationLine" />
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><EmailOptIn {...props} /></ComponentErrorBoundary>;
